import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Layout,
  useMiscellaneousRoutes,
  useUserStore,
} from '@roc/feature-app-core';
import { isNil } from '@roc/feature-utils';
import { StepObj } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useThirdPartyLoanRoutes } from '../hooks/useThirdPartyLoanRoutes';
import { useThirdPartyLoanStore } from '../hooks/useThirdPartyLoanStore';
import { routeByLoanSubType } from '../routes/thirdPartyLoanRoutes';
import {
  bridgeQuoteSteps,
  QuoteStep,
  SelectEntityOption,
} from '../utils/constants';
import { QuoteTypeStoreContext } from '../utils/quoteTypeStoreContext';
import { addressToString } from '../utils/quoteUtils';
import { CapitalProviderInfo } from './common/capitalProviderInfo';
import { OneToolQuoteStepsContainer } from './common/oneToolQuoteStepsContainer';
import { BorrowerInformationStep } from './fixFlip/borrowerInformationStep';
import { EconomicsStep } from './fixFlip/economicsStep';
import { EntityInformationStep } from './fixFlip/entityInformationStep';
import { FinalDetailsStep } from './fixFlip/finalDetailsStep';
import { FixFlipLeverageCard } from './fixFlip/fixFlipLeverageCard';
import {
  FixFlipLoanDetailsCard,
  FixFlipLoanDetailsCardNoAccordion,
} from './fixFlip/fixFlipLoanDetailsCard';
import { FixFlipPropertyInformationForm } from './fixFlip/fixFlipPropertyInformationForm';
import { LeverageStep } from './fixFlip/leverageStep';
import { LoanSuccessStep } from './fixFlip/loanSuccess';
import { PropertyInformationStep } from './fixFlip/propertyInformationStep';
import { SummaryStep } from './fixFlip/summaryStep';
import { StabilizedBridgePropertyInformationForm } from './stabilizedBridge/stabilizedBridgePropertyInformationForm';
import { StabilizedBridgeLeverageCard } from './stabilizedBridge/stabilizedBridgeLeverageCard';
import { StabilizedBridge } from 'libs/feature-loans/src/loanSubmission/stabilizedBridge/stabilizedBridge';
import { StabilizedBridgeLoanDetailsCard, StabilizedBridgeLoanDetailsCardNoAccordion } from './stabilizedBridge/stabilizedBridgeLoanDetailsCard';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(5, 7.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 0.5),
    },
  },
}));

interface Step extends StepObj {
  info?: string;
  size?: number;
}

export const StabilizedBridgeLoanSubmission = observer(() => {
  const { thirdPartyLoansStore } = useThirdPartyLoanStore();
  const { stabilizedBridgeStore } = thirdPartyLoansStore;
  const classes = useStyles();
  const { quoteNumber, loanId } = useParams<{ quoteNumber: string; loanId: string }>();
  const { push } = useHistory();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();

  const store = stabilizedBridgeStore;
  const quoteSteps = bridgeQuoteSteps;

  const lastCompletedStepIndex = quoteSteps.indexOf(store.lastCompletedStep);
  const { entityStore } = store;
  const { selectEntityOption } = entityStore;
  const savedQuoteNumber = store?.savedQuote?.quoteNumber;
  const isLoan = loanId;

  const title = isLoan
    ? `Edit Loan [${store.savedQuote?.loanNumber}] - Stabilized Property`
    : 'Submit a Loan - Stabilized Property';

  const { miscellaneousRoutesConfig } = useMiscellaneousRoutes();

  const disableNavigation = false;

  const init = async () => {
    store.reset();
    if (quoteNumber) {
      await store.loadQuote(quoteNumber);
    } else if (loanId) {
      await store.loadLoan(loanId);
    } else {
      const capitalProviderName = thirdPartyLoansStore.capitalProviderName;
      if (capitalProviderName === undefined) {
        push(thirdPartyLoanRoutesConfig.submitloan.url);
      } else {
        store.setCapitalProviderName(capitalProviderName);
      }
    }
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (store.quoteNotFound) {
      push(miscellaneousRoutesConfig.notFound.url);
    }
  }, [store.quoteNotFound]);

  useEffect(() => {
    if (!isLoan && savedQuoteNumber) {
      const route =
        thirdPartyLoanRoutesConfig[routeByLoanSubType[store.loanSubtype]];
      push(route(savedQuoteNumber).url);
    }
  }, [savedQuoteNumber]);

  if (isNil(store.step)) {
    return null;
  }

  const quoteData = store.getQuoteData();

  const borrowerInfo =
    lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.BORROWER_INFORMATION)
      ? quoteData.borrowers
        .map(b => [b.firstName, b.lastName].join(' '))
        .join(', ')
      : '';

  const propertyInfo =
    lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.PROPERTY_INFORMATION)
      ? quoteData.properties
        .map((p: any) => addressToString(p.address))
        .join(', ')
      : '';

  const leverageInfo =
    lastCompletedStepIndex >= quoteSteps.indexOf(QuoteStep.LEVERAGE)
      ? store.loanEconomicsStore.calculateKeyRatios()
      : '';

  const nextButtonText = 'SAVE & CONTINUE';

  const steps: Step[] = [
    {
      title: 'Entity Information',
      icon: null,
      onBack: () => store.entityStore.setSelectEntityOption(null),
      onNext: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
      allowBack:
        !!selectEntityOption &&
        selectEntityOption !== SelectEntityOption.NO_ENTITY,
      allowNext: !!selectEntityOption,
      nextButtonText: nextButtonText,
      info: borrowerInfo,
      size: 271,
    },
    {
      title: 'Borrower Information',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.ENTITY_INFORMATION),
      onNext: () => store.goToStep(QuoteStep.PROPERTY_INFORMATION),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      info: borrowerInfo,
      size: 271,
    },
    {
      title: 'Property Information',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.BORROWER_INFORMATION),
      onNext: () => store.goToStep(QuoteStep.LEVERAGE),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      info: propertyInfo,
      size: 400,
    },
    {
      title: 'Leverage',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.PROPERTY_INFORMATION),
      onNext: () => store.goToStep(QuoteStep.LOAN_ECONOMICS),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      info: leverageInfo,
      size: 130,
    },
    {
      title: 'Loan Economics',
      icon: null,
      onBack: () => store.goToStep(QuoteStep.LEVERAGE),
      onNext: () => store.goToStep(QuoteStep.SUMMARY),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      size: 130,
    },
    {
      title: 'Summary',
      icon: null,

      onBack: () => store.goToStep(QuoteStep.LOAN_ECONOMICS),
      onNext: () => store.goToStep(QuoteStep.FINAL_DETAILS),
      allowBack: true,
      allowNext: true,
      nextButtonText: nextButtonText,
      size: 115,
    },
  ];

  if (store.step === QuoteStep.FINAL_DETAILS) {
    return (
      <QuoteTypeStoreContext.Provider value={store}>
        <FinalDetailsStep store={store} />
      </QuoteTypeStoreContext.Provider>
    );
  }
  if (store.step === QuoteStep.SUCCESS) {
    return (
      <QuoteTypeStoreContext.Provider value={store}>
        <LoanSuccessStep store={store} />
      </QuoteTypeStoreContext.Provider>
    );
  }

  return (
    <QuoteTypeStoreContext.Provider value={store}>
      <Layout maxWidth={'lg'}>
        <Box display={'flex'} alignItems={'center'} pb={2}>
          <Typography
            align="left"
            variant="h2"
            style={{
              color: '#5E5E5E',
              fontSize: '48px',
            }}
          >
            {title}
          </Typography>
          <Box flex={1} />
          <CapitalProviderInfo capitalProvider={store.capitalProviderName} />
        </Box>
        <Paper className={classes.paper} elevation={0}>
          <OneToolQuoteStepsContainer
            activeStep={quoteSteps.indexOf(store.step)}
            disableNavigation={disableNavigation}
            steps={steps}
            handleStep={index => store.goToStep(quoteSteps[index])}
          >
            <EntityInformationStep />
            <BorrowerInformationStep store={store} />
            <PropertyInformationStep
              store={store}
              PropertyInformationForm={StabilizedBridgePropertyInformationForm}
            />
            <LeverageStep
              store={store as any}
              LeverageCard={StabilizedBridgeLeverageCard as any}
            />
            <EconomicsStep
              store={store as any}
              LoanDetailsCard={StabilizedBridgeLoanDetailsCard as any}
            />
            <SummaryStep
              store={store}
              LoanDetailsCard={StabilizedBridgeLoanDetailsCardNoAccordion as any}
            />
          </OneToolQuoteStepsContainer>
        </Paper>
      </Layout>
    </QuoteTypeStoreContext.Provider>
  );
});
